import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from '@/components/ui/command'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useTargetedAreaWebsocket } from '@/hooks/useTargetedAreaWebsocket'
import { cn } from '@/lib/utils'
import { useConnections } from '@/queries/connections/useConnections'
import { useGoogleAdsCampaigns } from '@/queries/connections/useGoogleAdsCampaigns'
import { useSyncToGoogleAds } from '@/queries/connections/useSyncToGoogleAds'
import { useAppDispatch, useAppSelector } from '@/redux/hooks/useAppState'
import { versionSelector } from '@/redux/store/slices/grid/gridSlice'
import { setIsSyncToGoogleDialogOpen } from '@/redux/store/slices/ui/uiSlice'
import { useStytchOrganization } from '@stytch/react/b2b'
import { GoogleAdsCampaign } from 'honeygrid-types/GoogleAds'
import { Connection } from 'honeygrid-types/validators-and-types/ConnectionTypes'
import { Check, ChevronsUpDown } from 'lucide-react'
import { FC, useEffect, useState } from 'react'

import { generateTargetingList } from '../Grids/Map/hooks/useExportTargeting'

interface SyncToGoogleDialogProps {
	isOpen: boolean
	onDismiss: () => void
}

export const SyncToGoogleDialog: FC<SyncToGoogleDialogProps> = ({ isOpen, onDismiss }) => {
	const [selectedConnection, setSelectedConnection] = useState<Connection>()
	const [selectedCampaign, setSelectedCampaign] = useState<GoogleAdsCampaign>()
	const [isFullReplace, setIsFullReplace] = useState(true)
	const [openConnection, setOpenConnection] = useState(false)
	const [openCampaign, setOpenCampaign] = useState(false)
	const { organization } = useStytchOrganization()

	const { data: connections, isLoading: isGetConnectionsLoading } = useConnections(
		organization?.organization_id
	)
	const { data: campaignsData, isLoading: isGetCampaignsLoading } = useGoogleAdsCampaigns({
		workspaceId: organization?.organization_id,
		connectionId: selectedConnection?.uuid ?? '',
	})
	const campaigns = campaignsData?.campaigns
	const { data: honeygridData } = useTargetedAreaWebsocket()
	const grid = useAppSelector(versionSelector)

	const syncToGoogleAdsMutation = useSyncToGoogleAds()
	const dispatch = useAppDispatch()

	const handleSyncClick = async () => {
		const cellIds = generateTargetingList(grid, honeygridData?.aggregatedDemographics)
		await syncToGoogleAdsMutation.mutateAsync({
			workspaceId: organization?.organization_id ?? '',
			connectionId: selectedConnection?.uuid ?? '',
			campaignId: selectedCampaign?.id ?? '',
			cellIds,
			isFullReplace,
		})
		dispatch(setIsSyncToGoogleDialogOpen(false))
	}

	useEffect(() => {
		if (connections && connections.length === 1) {
			setSelectedConnection(connections[0])
		}
	}, [connections])

	return (
		<Dialog open={isOpen} onOpenChange={onDismiss}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Sync microtargets to Google Ads</DialogTitle>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<div className="grid gap-2">
						<Label>Connection</Label>
						<Popover open={openConnection} onOpenChange={setOpenConnection}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									aria-expanded={openConnection}
									className="justify-between"
									disabled={isGetConnectionsLoading}
								>
									{selectedConnection ? selectedConnection.name : 'Select connection...'}
									<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[400px] p-0">
								<Command>
									<CommandInput placeholder="Search connections..." />
									<CommandEmpty>No connection found.</CommandEmpty>
									<CommandGroup>
										{connections?.map((connection: Connection) => (
											<CommandItem
												key={connection.id}
												onSelect={() => {
													setSelectedConnection(connection)
													setOpenConnection(false)
												}}
											>
												<Check
													className={cn(
														'mr-2 h-4 w-4',
														selectedConnection?.id === connection.id ? 'opacity-100' : 'opacity-0'
													)}
												/>
												{connection.name}
											</CommandItem>
										))}
									</CommandGroup>
								</Command>
							</PopoverContent>
						</Popover>
					</div>

					<div className="grid gap-2">
						<Label>Campaign</Label>
						<Popover open={openCampaign} onOpenChange={setOpenCampaign}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									aria-expanded={openCampaign}
									className="justify-between"
									disabled={isGetCampaignsLoading || !selectedConnection}
								>
									{selectedCampaign ? selectedCampaign.name : 'Select campaign...'}
									<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[400px] p-0">
								<Command>
									<CommandInput placeholder="Search campaigns..." />
									<CommandEmpty>No campaign found.</CommandEmpty>
									<CommandGroup>
										{campaigns?.map((campaign: GoogleAdsCampaign) => (
											<CommandItem
												key={campaign.id}
												onSelect={() => {
													setSelectedCampaign(campaign)
													setOpenCampaign(false)
												}}
											>
												<Check
													className={cn(
														'mr-2 h-4 w-4',
														selectedCampaign?.id === campaign.id ? 'opacity-100' : 'opacity-0'
													)}
												/>
												{campaign.name}
											</CommandItem>
										))}
									</CommandGroup>
								</Command>
							</PopoverContent>
						</Popover>
					</div>

					<div className="flex items-center space-x-2">
						<Checkbox
							id="replace"
							checked={isFullReplace}
							onCheckedChange={(checked) => setIsFullReplace(!!checked)}
						/>
						<Label htmlFor="replace">Replace existing targets</Label>
					</div>
				</div>
				<DialogFooter>
					<Button variant="ghost" onClick={onDismiss}>
						Cancel
					</Button>
					<Button
						onClick={handleSyncClick}
						disabled={!selectedConnection || !selectedCampaign || syncToGoogleAdsMutation.isPending}
					>
						{syncToGoogleAdsMutation.isPending ? 'Syncing...' : 'Sync microtargets'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
